import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Select, Button, Space, Card } from "antd";
import { Divider, Row, Col, Switch, Radio, Spin } from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import {
  getMessageTemplateById,
  updateMessageTemplate,
  type MessageTemplate,
} from "@api/services/facebook";
import { TemplatePreview } from "../TemplatePreview";
import { ROUTES } from "@config/routes.config";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";
import type{ FormValues } from "@interfaces/setting.interface";

const { Option } = Select;
const { TextArea } = Input;

const fetchingCache = new Map<string, Promise<MessageTemplate>>();
const fetchedTemplates = new Map<string, MessageTemplate>();

export const TemplateEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { success, error } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [template, setTemplate] = useState<MessageTemplate | null>(null);
  const [templateType, setTemplateType] = useState<string>("GENERIC");

  const currentIdRef = useRef<string | null>(null);

  const formValues = Form.useWatch([], form);

  const id = useMemo(() => {
    const idMatch = location.pathname.match(
      /\/settings\/facebook-templates\/(\d+)$/
    );
    return idMatch?.[1];
  }, [location.pathname]);

  useEffect(() => {
    if (!id) {
      error(
        "Lỗi đường dẫn",
        "Không tìm thấy ID template trong URL. Đang chuyển về trang danh sách.",
        3
      );
      navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATES);
      currentIdRef.current = null;
      return;
    }
    if (currentIdRef.current === id && template && template.id === Number(id)) {
      setFetching(false);
      return;
    }

    if (fetchingCache.has(id)) {
      currentIdRef.current = id;
      setFetching(true);

      const existingPromise = fetchingCache.get(id)!;

      existingPromise
        .then((foundTemplate) => {
          if (currentIdRef.current === id) {
            setTemplate(foundTemplate);
            setTemplateType(foundTemplate.type);
            fetchedTemplates.set(id, foundTemplate);
          }
        })
        .catch(() => {
          if (currentIdRef.current === id) {
            error(
              "Lỗi tải dữ liệu",
              "Không thể tải thông tin template. Vui lòng thử lại sau.",
              4
            );
          }
        })
        .finally(() => {
          if (currentIdRef.current === id) {
            setFetching(false);
          }
        });
      return;
    }

    if (fetchedTemplates.has(id)) {
      const cachedTemplate = fetchedTemplates.get(id)!;
      currentIdRef.current = id;
      setTemplate(cachedTemplate);
      setTemplateType(cachedTemplate.type);
      setFetching(false);
      return;
    }

    // Mark current ID
    currentIdRef.current = id;

    const fetchTemplate = async (): Promise<MessageTemplate> => {
      try {
        setFetching(true);
        const response = await getMessageTemplateById(Number(id));
        if (response.code === 0 && response.data) {
          let foundTemplate: MessageTemplate;
          if ("template" in response.data && response.data.template) {
            foundTemplate = response.data.template as MessageTemplate;
          } else {
            foundTemplate = response.data as MessageTemplate;
          }

          if (currentIdRef.current === id) {
            setTemplate(foundTemplate);
            setTemplateType(foundTemplate.type);
          }

          fetchedTemplates.set(id, foundTemplate);
          fetchingCache.delete(id);

          return foundTemplate;
        } else {
          fetchingCache.delete(id);

          if (currentIdRef.current === id) {
            error(
              "Không tìm thấy template",
              response.message || "Template không tồn tại hoặc đã bị xóa",
              4
            );
          }

          throw new Error(response.message || "Template không tồn tại");
        }
      } catch (err) {
        fetchingCache.delete(id);

        if (currentIdRef.current === id) {
          error(
            "Lỗi tải dữ liệu",
            "Không thể tải thông tin template. Vui lòng thử lại sau.",
            4
          );
        }

        throw err;
      } finally {
        if (currentIdRef.current === id) {
          setFetching(false);
        }
      }
    };

    const fetchPromise = fetchTemplate();
    fetchingCache.set(id, fetchPromise);

    return () => {
      if (currentIdRef.current === id) {
        currentIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!template) return;

    let payload: Record<string, unknown> = {};
    try {
      if (template.payload) {
        if (typeof template.payload === "string") {
          payload = JSON.parse(template.payload);
        } else {
          payload = template.payload as Record<string, unknown>;
        }
      }
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        payload = {};
      }
    } catch {
      payload = {};
    }

    const buttonsData: FormValues["buttons"] = Array.isArray(payload?.buttons)
      ? (payload.buttons as Array<Record<string, unknown>>).map((btn) => {
          // Determine button type: if has url, it's web_url, else if has payload, it's postback
          let buttonType: "web_url" | "postback" = "web_url";
          if (btn.type) {
            buttonType =
              (btn.type as string) === "postback" ? "postback" : "web_url";
          } else if (btn.url) {
            buttonType = "web_url";
          } else if (btn.payload) {
            buttonType = "postback";
          }

          return {
            type: buttonType,
            title: (btn.title as string) || "",
            url: (btn.url as string) || "",
            payload: (btn.payload as string) || "",
          };
        })
      : [];

    const formValues: FormValues = {
      name: template.name || "",
      type: template.type || "GENERIC",
      scope: template.scope || "USER",
      status: template.status === "ACTIVE",
      title: (payload?.title as string) || "",
      subtitle: (payload?.subtitle as string) || "",
      image_url: (payload?.image_url as string) || "",
      default_action_url:
        (payload?.default_action as { url?: string })?.url || "",
      buttons: buttonsData,
      text: (payload?.text as string) || "",
      media_type: (payload?.media_type as string) || "image",
      media_url: (payload?.media_url as string) || "",
      attachment_id: (payload?.attachment_id as string) || "",
      aspect_ratio: (payload?.aspect_ratio as string) || "horizontal",
    };

    form.resetFields();

    setTimeout(() => {
      form.setFieldsValue(formValues);

      if (Array.isArray(buttonsData) && buttonsData.length > 0) {
        setTimeout(() => {
          const currentValues = form.getFieldsValue();
          const currentButtons = currentValues.buttons as FormValues["buttons"];

          if (!currentButtons || currentButtons.length !== buttonsData.length) {
            form.setFieldValue("buttons", buttonsData);
          }

          buttonsData.forEach((btn, index) => {
            if (btn) {
              form.setFieldValue(
                ["buttons", index, "type"],
                btn.type || (btn.url ? "web_url" : "postback")
              );
              form.setFieldValue(["buttons", index, "title"], btn.title || "");
              if (btn.url) {
                form.setFieldValue(["buttons", index, "url"], btn.url);
              }
              if (
                btn.type === "postback" ||
                (btn.payload !== undefined && btn.payload !== null)
              ) {
                form.setFieldValue(
                  ["buttons", index, "payload"],
                  btn.payload || ""
                );
              }
            }
          });
        }, 200);
      }
    }, 100);
  }, [template, form]);

  const handleSubmit = async (values: FormValues) => {
    if (!id) return;

    try {
      setLoading(true);

      let payload: Record<string, unknown> = {};

      if (values.type === "GENERIC") {
        payload = {
          title: values.title,
          subtitle: values.subtitle,
          image_url: values.image_url,
          buttons: values.buttons,
        };
        if (values.default_action_url) {
          payload.default_action = {
            type: "web_url",
            url: values.default_action_url,
          };
        }
      } else if (values.type === "BUTTON") {
        payload = {
          text: values.text,
          buttons: values.buttons,
        };
      } else if (values.type === "MEDIA") {
        payload = {
          media_type: values.media_type || "image",
          media_url: values.media_url,
          attachment_id: values.attachment_id,
          aspect_ratio: values.aspect_ratio,
          buttons: values.buttons,
        };
      }

      // Only send allowed fields: name, payload, status
      const updateParams = {
        id: Number(id),
        name: values.name,
        payload,
        status: values.status ? "ACTIVE" : "INACTIVE",
      };

      const response = await updateMessageTemplate(updateParams);

      if (response.code === 0) {
        success(
          "Cập nhật thành công",
          "Mẫu template đã được cập nhật thành công.",
          3
        );
        navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATES);
      } else {
        error(
          "Cập nhật thất bại",
          response.message || "Không thể cập nhật mẫu template",
          4
        );
      }
    } catch {
      error(
        "Lỗi hệ thống",
        "Đã xảy ra lỗi khi cập nhật mẫu template. Vui lòng thử lại.",
        4
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATES);
  };

  const previewData = useMemo(() => {
    if (!formValues) return null;

    const values = formValues as {
      name?: string;
      type?: string;
      scope?: string;
      status?: boolean;
      title?: string;
      subtitle?: string;
      image_url?: string;
      default_action_url?: string;
      text?: string;
      media_type?: string;
      media_url?: string;
      attachment_id?: string;
      aspect_ratio?: string;
      buttons?: Array<{
        type: "web_url" | "postback";
        title: string;
        url?: string;
        payload?: string;
      }>;
    };
    let payload: Record<string, unknown> = {};

    if (values.type === "GENERIC") {
      payload = {
        title: values.title,
        subtitle: values.subtitle,
        image_url: values.image_url,
        buttons: values.buttons,
      };
      if (values.default_action_url) {
        payload.default_action = {
          type: "web_url",
          url: values.default_action_url,
        };
      }
    } else if (values.type === "BUTTON") {
      payload = {
        text: values.text,
        buttons: values.buttons,
      };
    } else if (values.type === "MEDIA") {
      payload = {
        media_type: values.media_type || "image",
        media_url: values.media_url,
        attachment_id: values.attachment_id,
        aspect_ratio: values.aspect_ratio,
        buttons: values.buttons,
      };
    }

    return {
      type: values.type || "GENERIC",
      ...payload,
    };
  }, [formValues]);

  const renderButtonsForm = () => (
    <Form.List name="buttons">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card key={key} size="small" className={styles.buttonCard}>
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, "type"]}
                    label="Loại"
                    rules={[{ required: true, message: "Chọn loại" }]}
                  >
                    <Select>
                      <Option value="web_url">URL</Option>
                      <Option value="postback">Postback</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, "title"]}
                    label="Tiêu đề nút"
                    rules={[{ required: true, message: "Nhập tiêu đề" }]}
                  >
                    <Input placeholder="Xem thêm" maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item shouldUpdate>
                    {({ getFieldValue }) => {
                      const buttonType = getFieldValue([
                        "buttons",
                        name,
                        "type",
                      ]);
                      return buttonType === "web_url" ? (
                        <Form.Item
                          {...restField}
                          name={[name, "url"]}
                          label="URL"
                          rules={[
                            { required: true, message: "Nhập URL" },
                            { type: "url", message: "URL không hợp lệ" },
                          ]}
                        >
                          <Input placeholder="https://example.com" />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          {...restField}
                          name={[name, "payload"]}
                          label="Payload"
                          rules={[{ required: true, message: "Nhập payload" }]}
                        >
                          <Input placeholder="GET_STARTED" />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
                <Col
                  span={1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingTop: 30,
                  }}
                >
                  <MinusCircleOutlined
                    onClick={() => remove(name)}
                    style={{
                      color: "#ff4d4f",
                      fontSize: 18,
                      cursor: "pointer",
                    }}
                  />
                </Col>
              </Row>
            </Card>
          ))}
          <Form.Item>
            <Button
              type="dashed"
              onClick={() => add({ type: "web_url" })}
              block
              icon={<PlusOutlined />}
              disabled={fields.length >= 3}
            >
              Thêm nút (tối đa 3)
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );

  if (fetching || !template) {
    return (
      <div className={styles.templateFormPage}>
        <div className={styles.templateFormLoading}>
          <Spin size="large" />
          <span>Đang tải template...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.templateFormPage}>
      <Row gutter={24} style={{ height: "100%" }}>
        <Col span={16} style={{ display: "flex", flexDirection: "column" }}>
          <Card
            title={<Space>Chỉnh sửa Mẫu Tin nhắn</Space>}
            className={styles.templateFormCard}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              key={template.id}
              preserve={false}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Tên Mẫu Tin nhắn"
                    rules={[
                      { required: true, message: "Vui lòng nhập tên mẫu!" },
                    ]}
                  >
                    <Input placeholder="VD: Chào mừng khách hàng mới" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="Loại Template"
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng chọn loại template!",
                      },
                    ]}
                  >
                    <Select disabled>
                      <Option value="GENERIC">Generic (Card)</Option>
                      <Option value="BUTTON">Button (Nút bấm)</Option>
                      <Option value="MEDIA">Media (Hình ảnh/Video)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="scope" label="Phạm vi">
                    <Select disabled>
                      <Option value="USER">Cá nhân (USER)</Option>
                      <Option value="ADMIN">Hệ thống (ADMIN)</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="status"
                    label="Trạng thái"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Hoạt động"
                      unCheckedChildren="Tắt"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Nội dung Template</Divider>

              {templateType === "GENERIC" && (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="title"
                        label="Tiêu đề"
                        rules={[{ required: true, message: "Nhập tiêu đề" }]}
                      >
                        <Input
                          placeholder="Xin chào!"
                          maxLength={80}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="subtitle" label="Phụ đề">
                        <Input
                          placeholder="Chào mừng bạn đến với dịch vụ"
                          maxLength={80}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="image_url"
                        label="URL Hình ảnh"
                        rules={[{ type: "url", message: "URL không hợp lệ" }]}
                      >
                        <Input placeholder="https://example.com/image.jpg" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="default_action_url"
                        label="URL khi click vào card"
                        rules={[{ type: "url", message: "URL không hợp lệ" }]}
                      >
                        <Input placeholder="https://example.com" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Các nút hành động">
                    {renderButtonsForm()}
                  </Form.Item>
                </>
              )}

              {templateType === "BUTTON" && (
                <>
                  <Form.Item
                    name="text"
                    label="Nội dung tin nhắn"
                    rules={[
                      { required: true, message: "Vui lòng nhập nội dung!" },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="Bạn muốn làm gì tiếp theo?"
                      maxLength={640}
                      showCount
                    />
                  </Form.Item>
                  <Form.Item label="Các nút hành động">
                    {renderButtonsForm()}
                  </Form.Item>
                </>
              )}

              {templateType === "MEDIA" && (
                <>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="media_type"
                        label="Loại Media"
                        initialValue="image"
                      >
                        <Radio.Group>
                          <Radio value="image">Hình ảnh</Radio>
                          <Radio value="video">Video</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="aspect_ratio"
                        label="Tỷ lệ khung hình"
                        initialValue="horizontal"
                      >
                        <Select>
                          <Option value="horizontal">Ngang (1.91:1)</Option>
                          <Option value="square">Vuông (1:1)</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="media_url"
                        label="URL Media"
                        rules={[{ type: "url", message: "URL không hợp lệ" }]}
                      >
                        <Input placeholder="https://example.com/image.jpg" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="attachment_id"
                        label="Attachment ID (tuỳ chọn)"
                      >
                        <Input placeholder="123456789..." />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Các nút hành động">
                    {renderButtonsForm()}
                  </Form.Item>
                </>
              )}
            </Form>
          </Card>
        </Col>
        <Col span={8} style={{ display: "flex", flexDirection: "column" }}>
          <Card
            title="Xem trước trên Messenger"
            className={styles.templatePreviewCard}
          >
            <div className={styles.templatePreviewWrapper}>
              <TemplatePreview data={previewData || undefined} />
            </div>
          </Card>
        </Col>
      </Row>
      <div className={styles.templateFormFooter}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            disabled={loading}
          >
            Quay lại
          </Button>
          <Button
            type="primary"
            onClick={async () => {
              const values = await form.validateFields();
              await handleSubmit(values as FormValues);
            }}
            loading={loading}
          >
            Cập nhật
          </Button>
        </Space>
      </div>
    </div>
  );
};
